import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { 
  Gift, 
  Ticket, 
  Zap, 
  Star, 
  ShoppingBag, 
  Stamp,
  TrendingUp,
  Users,
  Euro,
  Activity
} from 'lucide-react';
import axios from 'axios';

// Import sub-components
import PromoCodesTab from './promotions/PromoCodesTab';
import HappyHoursTab from './promotions/HappyHoursTab';
import LoyaltyTab from './promotions/LoyaltyTab';
import BogoTab from './promotions/BogoTab';
import StampCardTab from './promotions/StampCardTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromotionsHub() {
  useAuth();
  const [activeTab, setActiveTab] = useState('promo-codes');
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    const fetchGlobalStats = async () => {
      try {
        const response = await axios.get(`${API}/promotions/global-stats`);
        setStats(response.data);
      } catch (err) {
        console.error('Error fetching global stats:', err);
      } finally {
        setLoadingStats(false);
      }
    };
    fetchGlobalStats();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gift className="w-7 h-7 text-emerald-500" />
            Promotions & Fidélité
          </h1>
          <p className="text-gray-500 mt-1">Gérez tous vos outils de fidélisation et promotions</p>
        </div>

        {/* Global Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Promos Actives</p>
                <p className="text-3xl font-bold mt-1">
                  {loadingStats ? '...' : stats?.overview?.total_active_promotions || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Économies Clients</p>
                <p className="text-3xl font-bold mt-1">
                  {loadingStats ? '...' : `${stats?.overview?.total_customer_savings || 0}€`}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Euro className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Utilisations</p>
                <p className="text-3xl font-bold mt-1">
                  {loadingStats ? '...' : stats?.overview?.total_promotions_used || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm font-medium">Clients Fidèles</p>
                <p className="text-3xl font-bold mt-1">
                  {loadingStats ? '...' : stats?.overview?.total_loyalty_members || 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats Row */}
        {stats && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Ticket className="w-4 h-4 text-emerald-500" />
                <span className="text-gray-600">Codes:</span>
                <span className="font-semibold">{stats.promo_codes?.active || 0} actifs</span>
                <span className="text-gray-400">({stats.promo_codes?.total_uses || 0} utilisés)</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span className="text-gray-600">Happy Hours:</span>
                <span className="font-semibold">{stats.happy_hours?.active || 0}</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                <span className="text-gray-600">Fidélité:</span>
                <span className="font-semibold">{stats.loyalty?.enabled ? 'Actif' : 'Inactif'}</span>
                {stats.loyalty?.enabled && (
                  <span className="text-gray-400">({stats.loyalty?.members || 0} membres)</span>
                )}
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <ShoppingBag className="w-4 h-4 text-purple-500" />
                <span className="text-gray-600">BOGO:</span>
                <span className="font-semibold">{stats.bogo?.active || 0} offres</span>
              </div>
              <div className="w-px h-4 bg-gray-200" />
              <div className="flex items-center gap-2">
                <Stamp className="w-4 h-4 text-orange-500" />
                <span className="text-gray-600">Tampons:</span>
                <span className="font-semibold">{stats.stamp_card?.enabled ? 'Actif' : 'Inactif'}</span>
                {stats.stamp_card?.enabled && (
                  <span className="text-gray-400">({stats.stamp_card?.active_cards || 0} cartes)</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex flex-wrap h-auto gap-1 bg-gray-100 p-1 rounded-xl">
            <TabsTrigger 
              value="promo-codes" 
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg"
            >
              <Ticket className="w-4 h-4" />
              <span className="hidden sm:inline">Codes Promo</span>
              <span className="sm:hidden">Codes</span>
            </TabsTrigger>
            <TabsTrigger 
              value="happy-hours" 
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Happy Hours & Formules</span>
              <span className="sm:hidden">Happy Hours</span>
            </TabsTrigger>
            <TabsTrigger 
              value="loyalty" 
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg"
            >
              <Star className="w-4 h-4" />
              <span className="hidden sm:inline">Points Fidélité</span>
              <span className="sm:hidden">Fidélité</span>
            </TabsTrigger>
            <TabsTrigger 
              value="bogo" 
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">X Achetés = Y Offert</span>
              <span className="sm:hidden">X=Y</span>
            </TabsTrigger>
            <TabsTrigger 
              value="stamp-card" 
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 py-2.5 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg"
            >
              <Stamp className="w-4 h-4" />
              <span className="hidden sm:inline">Carte à Tampons</span>
              <span className="sm:hidden">Tampons</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="promo-codes" className="mt-6">
            <PromoCodesTab />
          </TabsContent>

          <TabsContent value="happy-hours" className="mt-6">
            <HappyHoursTab />
          </TabsContent>

          <TabsContent value="loyalty" className="mt-6">
            <LoyaltyTab />
          </TabsContent>

          <TabsContent value="bogo" className="mt-6">
            <BogoTab />
          </TabsContent>

          <TabsContent value="stamp-card" className="mt-6">
            <StampCardTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
